/*
 * RS main product: tabs under the product and the size guide popup.
 * - <rs-product-tabs>: tabs on desktop (and on mobile when chosen), accordion on mobile. Without
 *   this script every panel is simply shown. Keyboard: arrow keys, Home, End. The theme editor
 *   opens the tab of the selected block.
 * - Size guide: the link opens its <dialog>; without the script the link opens the image itself.
 * - <rs-back-in-stock> (snippets/rs-back-in-stock.liquid): shown when the selected variant is sold
 *   out (slide up), hidden when it is available; requested size, code and link follow the variant.
 *   Sends in the background (fetch): Shopify answers with the page, the thank-you text replaces the
 *   form. Spam check (captcha) or anything unexpected: normal sending with a page reload.
 * - <rs-pickup> (snippets/rs-pickup.liquid): on variant change takes the block from the section
 *   HTML that Dawn's product-info.js fetched for the new variant (no extra request).
 * - <rs-product-gallery> (snippets/rs-product-gallery.liquid): main image, thumbnails slider,
 *   lightbox and zoom on mouse over; shows the variant's image when the variant changes.
 */
if (!customElements.get('rs-product-tabs')) {
  customElements.define(
    'rs-product-tabs',
    class RsProductTabs extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('.rs-mp__tablist');
        this.tabs = Array.from(this.querySelectorAll('.rs-mp__tab'));
        this.wraps = Array.from(this.querySelectorAll('.rs-mp__panel-wrap'));
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.accordion = this.classList.contains('rs-mp__tabs--mobile-accordion');

        this.tablist.hidden = false;
        this.classList.add('is-ready');

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab.dataset.blockId));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });
        this.wraps.forEach((wrap) => {
          const toggle = wrap.querySelector('.rs-mp__panel-toggle');
          toggle.addEventListener('click', () => this.toggle(wrap));
          this.setOpen(wrap, toggle.getAttribute('aria-expanded') === 'true');
        });

        const first = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        if (first) this.select(first.dataset.blockId, false);

        this.onBlockSelect = (event) => {
          if (this.wraps.some((wrap) => wrap.dataset.blockId === event.detail.blockId)) {
            this.select(event.detail.blockId, false);
            const wrap = this.wraps.find((item) => item.dataset.blockId === event.detail.blockId);
            if (wrap) this.setOpen(wrap, true);
          }
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      select(blockId, focus) {
        this.tabs.forEach((tab) => {
          const active = tab.dataset.blockId === blockId;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        this.wraps.forEach((wrap) => wrap.classList.toggle('is-active', wrap.dataset.blockId === blockId));
      }

      toggle(wrap) {
        const toggle = wrap.querySelector('.rs-mp__panel-toggle');
        this.setOpen(wrap, toggle.getAttribute('aria-expanded') !== 'true');
      }

      setOpen(wrap, open) {
        wrap.querySelector('.rs-mp__panel-toggle').setAttribute('aria-expanded', open ? 'true' : 'false');
        wrap.classList.toggle('is-open', open);
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        let next = null;
        if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
        if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        if (event.key === 'Home') next = this.tabs[0];
        if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        this.select(next.dataset.blockId, true);
      }
    }
  );
}

if (!window.rsMainProductDialogs) {
  window.rsMainProductDialogs = true;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-rs-mp-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.rsMpOpen);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      dialog.showModal();
      document.body.classList.add('rs-mp-dialog-open');
      return;
    }

    const closer = event.target.closest('[data-rs-mp-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // Click on the dark backdrop (outside the box) closes the dialog.
    if (event.target.matches?.('dialog.rs-mp__dialog')) {
      const box = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) event.target.close();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches?.('dialog.rs-mp__dialog')) document.body.classList.remove('rs-mp-dialog-open');
    },
    true
  );
}

if (!customElements.get('rs-product-gallery')) {
  customElements.define(
    'rs-product-gallery',
    class RsProductGallery extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.rs-gal__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.rs-gal__thumb'));
        this.track = this.querySelector('.rs-gal__track');
        this.dots = Array.from(this.querySelectorAll('.rs-gal__dot'));
        this.current = this.querySelector('[data-rs-gal-current]');
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.zoom = this.dataset.zoom;
        this.speed = () => parseFloat(getComputedStyle(this).getPropertyValue('--rs-mp-media-speed')) || 500;

        this.querySelector('[data-rs-gal-prev]')?.addEventListener('click', () => this.go(this.index - 1, 'prev'));
        this.querySelector('[data-rs-gal-next]')?.addEventListener('click', () => this.go(this.index + 1, 'next'));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.index))));

        if (this.track) {
          this.querySelector('[data-rs-thumbs-prev]')?.addEventListener('click', () => this.scrollThumbs(-1));
          this.querySelector('[data-rs-thumbs-next]')?.addEventListener('click', () => this.scrollThumbs(1));
          this.track.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
          window.addEventListener('resize', (this.onResize = () => this.updateThumbArrows()));
          this.updateThumbArrows();
        }

        this.initSwipe(this.querySelector('.rs-gal__stage'), (dir) => this.go(this.index + dir, dir > 0 ? 'next' : 'prev'));
        this.querySelector('.rs-gal__main').addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(this.index - 1, 'prev');
          if (event.key === 'ArrowRight') this.go(this.index + 1, 'next');
        });

        this.initZoom();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.section) return;
            const mediaId = data.variant?.featured_media?.id;
            if (!mediaId) return;
            const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
            if (target > -1) this.go(target);
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      go(target, dir) {
        const count = this.slides.length;
        if (count < 2) return;
        const next = (target + count) % count;
        if (next === this.index) return;
        this.dataset.dir = dir || (next > this.index ? 'next' : 'prev');
        window.pauseAllMedia?.();

        const old = this.slides[this.index];
        old.classList.add('is-leaving');
        window.clearTimeout(old.leaveTimer);
        old.leaveTimer = window.setTimeout(() => old.classList.remove('is-leaving'), this.speed());

        this.slides.forEach((slide, i) => {
          const active = i === next;
          slide.classList.toggle('is-active', active);
          if (active) slide.classList.remove('is-leaving');
          slide.toggleAttribute('aria-hidden', !active);
          slide.querySelector('.rs-gal__open')?.setAttribute('tabindex', active ? '0' : '-1');
        });
        this.thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === next));
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === next));
        if (this.current) this.current.textContent = next + 1;
        this.index = next;
        this.showThumb(next);
      }

      // Thumbnails slider (row or column): scroll inside the track only, never the page.
      isVertical() {
        return this.track && getComputedStyle(this.track).flexDirection === 'column';
      }

      showThumb(i) {
        const item = this.thumbs[i]?.parentElement;
        if (!item || !this.track) return;
        const vertical = this.isVertical();
        const start = vertical ? item.offsetTop : item.offsetLeft;
        const size = vertical ? item.offsetHeight : item.offsetWidth;
        const view = vertical ? this.track.clientHeight : this.track.clientWidth;
        const pos = vertical ? this.track.scrollTop : this.track.scrollLeft;
        if (start < pos || start + size > pos + view) {
          this.track.scrollTo(vertical ? { top: start } : { left: start });
        }
      }

      scrollThumbs(dir) {
        const item = this.track.firstElementChild;
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        if (this.isVertical()) {
          this.track.scrollBy({ top: dir * (item.offsetHeight + gap) });
        } else {
          this.track.scrollBy({ left: dir * (item.offsetWidth + gap) });
        }
      }

      updateThumbArrows() {
        const vertical = this.isVertical();
        const pos = vertical ? this.track.scrollTop : this.track.scrollLeft;
        const max = vertical
          ? this.track.scrollHeight - this.track.clientHeight
          : this.track.scrollWidth - this.track.clientWidth;
        const prev = this.querySelector('[data-rs-thumbs-prev]');
        const next = this.querySelector('[data-rs-thumbs-next]');
        if (prev) prev.disabled = pos <= 1;
        if (next) next.disabled = pos >= max - 1;
      }

      initSwipe(element, onSwipe) {
        if (!element) return;
        let x = null;
        let y = null;
        element.addEventListener(
          'touchstart',
          (event) => {
            x = event.touches[0].clientX;
            y = event.touches[0].clientY;
          },
          { passive: true }
        );
        element.addEventListener('touchend', (event) => {
          if (x === null) return;
          const dx = event.changedTouches[0].clientX - x;
          const dy = event.changedTouches[0].clientY - y;
          x = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            this.swiped = Date.now();
            onSwipe(dx < 0 ? 1 : -1);
          }
        });
      }

      // Zoom: lightbox on click, or zoom on mouse over (a tap opens the lightbox on touch screens).
      initZoom() {
        this.lightbox = this.querySelector('.rs-lb');
        const fineMouse = window.matchMedia('(hover: hover) and (pointer: fine)');

        this.querySelectorAll('.rs-gal__open').forEach((button) => {
          button.addEventListener('click', () => {
            if (this.swiped && Date.now() - this.swiped < 400) return;
            if (this.zoom === 'lightbox') this.openLightbox(button);
            if (this.zoom === 'hover' && !fineMouse.matches && this.dataset.tapLightbox === 'true') {
              this.openLightbox(button);
            }
          });

          if (this.zoom !== 'hover') return;
          button.addEventListener('mouseenter', () => fineMouse.matches && button.classList.add('is-zooming'));
          button.addEventListener('mouseleave', () => button.classList.remove('is-zooming'));
          button.addEventListener('mousemove', (event) => {
            if (!fineMouse.matches) return;
            const rect = button.getBoundingClientRect();
            button.style.setProperty('--rs-gal-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
            button.style.setProperty('--rs-gal-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
          });
        });

        if (!this.lightbox) return;
        this.lbImages = Array.from(this.querySelectorAll('.rs-gal__open')).map((button) => ({
          src: button.dataset.full,
          alt: button.querySelector('img')?.alt || '',
          slide: this.slides.indexOf(button.closest('.rs-gal__slide')),
        }));
        this.lbImg = this.lightbox.querySelector('.rs-lb__img');
        this.lbCurrent = this.lightbox.querySelector('[data-rs-lb-current]');

        this.lightbox.querySelector('[data-rs-lb-close]').addEventListener('click', () => this.closeLightbox());
        this.lightbox.querySelector('[data-rs-lb-prev]')?.addEventListener('click', () => this.showLightbox(this.lbIndex - 1));
        this.lightbox.querySelector('[data-rs-lb-next]')?.addEventListener('click', () => this.showLightbox(this.lbIndex + 1));
        this.lightbox.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeLightbox();
        });
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.showLightbox(this.lbIndex - 1);
          if (event.key === 'ArrowRight') this.showLightbox(this.lbIndex + 1);
        });
        // A click on the empty area around the image closes the lightbox.
        this.lightbox.querySelector('.rs-lb__stage').addEventListener('click', (event) => {
          if (event.target === event.currentTarget && !(this.swiped && Date.now() - this.swiped < 400)) {
            this.closeLightbox();
          }
        });
        this.initSwipe(this.lightbox, (dir) => this.showLightbox(this.lbIndex + dir));
      }

      openLightbox(button) {
        if (!this.lightbox) return;
        const index = this.lbImages.findIndex((image) => image.slide === this.slides.indexOf(button.closest('.rs-gal__slide')));
        this.showLightbox(Math.max(0, index), true);
        this.lightbox.showModal();
        document.body.classList.add('rs-mp-dialog-open');
        requestAnimationFrame(() => requestAnimationFrame(() => this.lightbox.classList.add('is-open')));
      }

      closeLightbox() {
        if (!this.lightbox?.open) return;
        this.lightbox.classList.remove('is-open');
        const speed = parseFloat(getComputedStyle(this.lightbox).transitionDuration) * 1000 || 0;
        window.setTimeout(() => {
          this.lightbox.close();
          document.body.classList.remove('rs-mp-dialog-open');
        }, speed);
        // The main image follows the last image seen in the lightbox.
        const slide = this.lbImages[this.lbIndex]?.slide;
        if (slide > -1) this.go(slide);
      }

      showLightbox(target, instant) {
        const count = this.lbImages.length;
        const index = (target + count) % count;
        const image = this.lbImages[index];
        this.lbIndex = index;
        if (this.lbCurrent) this.lbCurrent.textContent = index + 1;
        const swap = () => {
          this.lbImg.onload = () => this.lbImg.classList.remove('is-loading');
          this.lbImg.src = image.src;
          this.lbImg.alt = image.alt;
          if (this.lbImg.complete) this.lbImg.classList.remove('is-loading');
        };
        if (instant) {
          swap();
        } else {
          this.lbImg.classList.add('is-loading');
          window.setTimeout(swap, 150);
        }
        // Preload the neighbours.
        [index - 1, index + 1].forEach((i) => {
          const near = this.lbImages[(i + count) % count];
          if (near) new Image().src = near.src;
        });
      }
    }
  );
}

if (!customElements.get('rs-back-in-stock')) {
  customElements.define(
    'rs-back-in-stock',
    class RsBackInStock extends HTMLElement {
      connectedCallback() {
        // The load animation runs once; later changes use the transition.
        this.addEventListener('animationend', () => this.classList.remove('is-initial'), { once: true });
        this.form = this.closest('form');
        this.form?.addEventListener('submit', (event) => this.onSubmit(event));
        this.popup = this.querySelector('.rs-mp__notify-popup');
        if (this.popup) {
          this.popup.querySelector('[data-rs-notify-close]')?.addEventListener('click', () => this.closePopup());
          this.popup.addEventListener('cancel', (event) => {
            event.preventDefault();
            this.closePopup();
          });
          // Click on the dark area around the box closes it.
          this.popup.addEventListener('click', (event) => {
            if (event.target !== this.popup) return;
            const box = this.popup.getBoundingClientRect();
            const inside =
              event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
            if (!inside) this.closePopup();
          });
          // Sent the normal way (page reload): open the popup on the reloaded page.
          if (this.popup.hasAttribute('data-open-on-load')) this.openPopup();
        }
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId !== this.dataset.section || !data.variant) return;
          const variant = data.variant;
          this.classList.remove('is-initial');
          if (this.querySelector('.rs-mp__notify-success')) return;
          this.classList.toggle('is-visible', !variant.available);
          const set = (selector, value) => {
            const input = this.querySelector(selector);
            if (input) input.value = value;
          };
          set('[data-rs-notify-size]', this.dataset.defaultOnly === 'true' ? '-' : variant.title || '');
          set('[data-rs-notify-sku]', variant.sku || '');
          set('[data-rs-notify-link]', `${window.location.origin}${window.location.pathname}?variant=${variant.id}`);
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      async onSubmit(event) {
        if (this.sending || !window.fetch || !window.DOMParser) return;
        event.preventDefault();
        const button = this.querySelector('.rs-mp__notify-button');
        const error = this.querySelector('.rs-mp__notify-error');
        this.sending = true;
        button?.setAttribute('aria-busy', 'true');
        if (error) error.hidden = true;

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
          });
          // Spam check page: let Shopify handle it with a normal submit.
          if (response.url.includes('/challenge')) throw new Error('challenge');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sent = response.url.includes('contact_posted=true') || html.querySelector('.rs-mp__notify-success');
          if (sent) {
            this.showSuccess();
            return;
          }
          const message = html.querySelector('.rs-mp__notify-error:not([hidden])');
          if (message && error) {
            error.innerHTML = message.innerHTML;
            error.hidden = false;
            this.querySelector('.rs-mp__notify-input')?.setAttribute('aria-invalid', 'true');
            return;
          }
          throw new Error('unexpected answer');
        } catch (e) {
          // Anything unexpected: send the normal way (page reload).
          this.form.submit();
        } finally {
          this.sending = false;
          button?.removeAttribute('aria-busy');
        }
      }

      openPopup() {
        if (!this.popup || this.popup.open) return;
        this.popup.showModal();
        document.body.classList.add('rs-mp-dialog-open');
        requestAnimationFrame(() => requestAnimationFrame(() => this.popup.classList.add('is-open')));
      }

      closePopup() {
        if (!this.popup?.open) return;
        this.popup.classList.remove('is-open');
        const speed = parseFloat(getComputedStyle(this.popup).transitionDuration) * 1000 || 0;
        window.setTimeout(() => {
          this.popup.close();
          document.body.classList.remove('rs-mp-dialog-open');
        }, speed);
      }

      showSuccess() {
        if (this.popup) {
          const input = this.querySelector('.rs-mp__notify-input');
          if (input) {
            input.value = '';
            input.removeAttribute('aria-invalid');
          }
          this.openPopup();
          return;
        }
        const inner = this.querySelector('.rs-mp__notify-inner');
        const template = this.querySelector('template[data-rs-notify-success]');
        if (!inner || !template) return;
        inner.replaceChildren(template.content.cloneNode(true));
        inner.querySelector('.rs-mp__notify-success')?.focus?.();
      }
    }
  );
}

if (!customElements.get('rs-pickup')) {
  customElements.define(
    'rs-pickup',
    class RsPickup extends HTMLElement {
      connectedCallback() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data?.sectionId !== this.dataset.section || !data.html) return;
          const fresh = data.html.getElementById(this.id);
          if (!fresh) return;
          this.innerHTML = fresh.innerHTML;
          this.classList.toggle('hidden', fresh.classList.contains('hidden'));
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }
    }
  );
}
